import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Image } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { generalConfigApi } from '../../../../apis';
import ButtonWrap from '../../../../components/ButtonWrap';
import ToastError from '../../../../components/Toast/ToastError';
import ToastSuccess from '../../../../components/Toast/ToastSuccess';
import Widget from '../../../../components/Widget';
import { CONFIG_LOGO } from '../../../../constants/QueryKey';
import { RootState } from '../../../../store';
import './style.scss';

const UploadLogo = () => {
  const intl = useIntl();
  const [imageSrc, setImageSrc] = useState<string>('');
  const [file, setFile] = useState<File | undefined>();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const logo = useSelector((state: RootState) => state.setting.logo);
  const queryClient = useQueryClient();
  const updateLogoMutation = useMutation({
    mutationFn: (payload: File) => generalConfigApi.generalConfigControllerUpdateLogoConfig(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CONFIG_LOGO] });
      ToastSuccess({
        content: intl.formatMessage({ id: 'upload.logo.notification.success' }),
      });
    },
    onError: (error) => {
      ToastError({
        content: intl.formatMessage({ id: 'upload.logo.notification.error' }),
      });
    },
  });
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        ToastError({
          content: intl.formatMessage({ id: 'upload.logo.notification.upload' }),
        });
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        ToastError({
          content: intl.formatMessage({ id: 'upload.logo.notification.size' }),
        });
        return;
      }
    }
    setFile(file);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImageSrc(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };
  const handleUpdateLogo = () => {
    if (file) {
      updateLogoMutation.mutate(file);
    }
  };
  useEffect(() => {
    if (logo) {
      setImageSrc(logo);
    } else {
      setImageSrc('images/mos-main.png');
    }
  }, [logo]);

  return (
    <div className="upload-logo">
      <Widget
        breadcrumbs={[intl.formatMessage({ id: 'upload.logo.title' })]}
        content={
          <div className="upload-logo_content">
            <Image
              className="upload-logo_content-image"
              src={imageSrc}
              alt={logo || 'images/mos-main.png'}
              fallback="images/mos-main.png"
              preview
              width={241}
              height={241}
            />
            <input
              type="file"
              accept="image/*"
              className="upload-logo_content-hidden"
              ref={fileInputRef}
              onChange={handleFileChange}
            />
            <ButtonWrap className="upload-logo_content-upload" onClick={() => fileInputRef.current?.click()}>
              {intl.formatMessage({ id: 'upload.logo.upload' })}
            </ButtonWrap>
            <ButtonWrap
              disabled={updateLogoMutation.isPending}
              className="upload-logo_content-push"
              onClick={handleUpdateLogo}
            >
              {intl.formatMessage({ id: 'upload.logo.push' })}
            </ButtonWrap>
          </div>
        }
        title={intl.formatMessage({ id: 'upload.logo.header' })}
      />
    </div>
  );
};

export default UploadLogo;
